import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Globe, ExternalLink, ShoppingBag } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

export const trackPartnerContact = (partner, channel) =>
  trackEvent('partner_contact_click', { resource: `${partner} · ${channel}` });
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

export const PREFERRED_PARTNERS = [
  {
    id: 'barfield',
    name: 'Barfield, Inc.',
    logo: '/images/barfield-colored-logo-transparent.jpg',
    blurb:
      'A preferred global distribution partner with decades of expertise in aviation ground support and test equipment. Barfield brings CTRL ultrasonic sensors and acoustic imaging cameras to airlines, MROs, and aviation service providers worldwide.',
    email: 'gstesales@barfieldinc.com',
    phone: '+1 (305) 894-5400',
    website: 'https://www.barfieldinc.com',
    location: 'Miami, Florida, USA',
    note: 'For GSTE product inquiries, pricing, and availability.',
  },
  {
    id: 'avionteq',
    name: 'AvionTEq',
    logo: '/images/avionteq-logo.jpg',
    blurb:
      'A preferred distribution partner and FAA-certified repair station specializing in avionics and aircraft test equipment sales, calibration, and support for avionics shops, MROs, and operators.',
    email: 'sales@avionteq.net',
    phone: '+1 (310) 649-0400',
    phoneAlt: '+1 (888) 649-0080',
    website: 'https://www.avionteq.com',
    location: '7240 Hayvenhurst Pl, Van Nuys Airport, Van Nuys, California 91406 USA',
    note: 'For equipment sales, calibration, and repair.',
  },
];

export const OTHER_PARTNERS = [
  { name: 'Bocatronics', slug: 'bocatronics', website: 'https://www.bocatronics.com', location: 'Boca Raton, FL, USA', blurb: 'Aviation electronics and aircraft parts distribution partner serving operators and MROs.' },
  { name: 'Fairhope Aerospace', slug: 'fairhope-aerospace', website: 'https://www.fairhopeaero.com', logo: '/partners/fairhope.png', email: 'inforequest@fhaero.com', phone: '+1 (251) 928-8800', location: 'Fairhope, AL, USA', blurb: 'Repair and overhaul of components and LRUs for commercial, business, and military aircraft.' },
  { name: 'NT Y-Max (Japan)', slug: 'nt-y-max', website: 'https://nt-ymax.co.jp/en/', phone: '+81-3-3945-5092', location: 'Sugamo, Toshima-ku, Tokyo, Japan', blurb: 'Japanese distributor of imported test and measurement equipment for aerospace and defense maintenance.' },
  { name: 'Pacific Air Industries', slug: 'pacific-air-industries', website: 'https://www.pac-air.com', logo: '/partners/pacair.png', email: 'sales@pac-air.com', location: 'Chatsworth, CA, USA', blurb: 'Aircraft parts and support with 24-hour AOG service for Boeing, Airbus, and Douglas fleets.' },
  { name: 'Precise Tool & Gage', slug: 'precise-tool-gage', website: 'https://www.precisetoolco.com', logo: '/partners/precise-tool.png', location: 'Preston, WA, USA', blurb: 'Precision tooling and gauging supplier serving aviation maintenance and manufacturing.' },
];

const Row = ({ icon: Icon, children, testid }) => (
  <li className="flex items-start gap-3 text-slate-700" data-testid={testid}>
    <Icon className="w-4 h-4 mt-1 text-blue-600 flex-shrink-0" aria-hidden="true" />
    <span className="text-sm sm:text-base leading-relaxed break-words">{children}</span>
  </li>
);

export const PartnerContactTabs = () => (
  <Tabs defaultValue={PREFERRED_PARTNERS[0].id} className="w-full" data-testid="partner-contact-tabs">
    <TabsList className="grid w-full grid-cols-2 h-auto p-1 bg-slate-100">
      {PREFERRED_PARTNERS.map((p) => (
        <TabsTrigger key={p.id} value={p.id} className="py-2.5 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-700" data-testid={`partner-tab-${p.id}`}>
          {p.name}
        </TabsTrigger>
      ))}
    </TabsList>
    {PREFERRED_PARTNERS.map((p) => (
      <TabsContent key={p.id} value={p.id} className="mt-4" data-testid={`partner-tab-content-${p.id}`}>
        <div className="bg-blue-50 border-l-4 border-blue-600 p-5 sm:p-6 rounded-r-lg">
          <p className="font-bold text-[#1e2d3b] mb-1">Contact {p.name}</p>
          <p className="text-sm text-slate-600 mb-4">{p.note}</p>
          <ul className="space-y-2.5">
            <Row icon={Mail} testid={`partner-email-${p.id}`}>
              <a href={`mailto:${p.email}`} onClick={() => trackPartnerContact(p.name, 'email')} className="font-semibold text-blue-700 hover:underline">{p.email}</a>
            </Row>
            {p.phone && (
              <Row icon={Phone} testid={`partner-phone-${p.id}`}>
                <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`} onClick={() => trackPartnerContact(p.name, 'phone')} className="hover:underline">{p.phone}</a>
                {p.phoneAlt && <span className="text-slate-500"> · {p.phoneAlt}</span>}
              </Row>
            )}
            <Row icon={MapPin} testid={`partner-location-${p.id}`}>{p.location}</Row>
            <Row icon={Globe} testid={`partner-website-${p.id}`}>
              <a href={p.website} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1">
                {p.website.replace('https://', '')} <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            </Row>
          </ul>
        </div>
      </TabsContent>
    ))}
  </Tabs>
);

export const PreferredPartnerCard = ({ partner }) => (
  <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 flex flex-col" data-testid={`preferred-partner-${partner.id}`}>
    <span className="inline-flex self-start items-center rounded-full bg-orange-100 text-orange-700 px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-5">
      Preferred Distribution Partner
    </span>
    <div className="h-16 flex items-center mb-5">
      <img src={partner.logo} alt={partner.name} className="max-h-16 w-auto object-contain" loading="lazy" />
    </div>
    <h3 className="text-xl font-bold text-[#1e2d3b] mb-3">{partner.name}</h3>
    <p className="text-slate-700 leading-relaxed max-w-prose flex-grow">{partner.blurb}</p>
    <ul className="mt-5 space-y-1.5 text-sm text-slate-600">
      <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" aria-hidden="true" />{partner.location}</li>
      {partner.phoneAlt && <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" aria-hidden="true" />{partner.phone} · {partner.phoneAlt}</li>}
    </ul>
    <div className="cta-group cta-group-sm mt-6" data-testid={`preferred-partner-contacts-${partner.id}`}>
      <a href={`mailto:${partner.email}`} onClick={() => trackPartnerContact(partner.name, 'email')} className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors" data-testid={`partner-email-btn-${partner.id}`}>
        <Mail className="w-4 h-4" aria-hidden="true" /> Email {partner.name.replace(', Inc.', '')}
      </a>
      {partner.phone && (
        <a href={`tel:${partner.phone.replace(/[^\d+]/g, '')}`} onClick={() => trackPartnerContact(partner.name, 'phone')} className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-800 text-sm font-semibold transition-colors" data-testid={`partner-phone-btn-${partner.id}`}>
          <Phone className="w-4 h-4" aria-hidden="true" /> {partner.phone}
        </a>
      )}
      <a href={partner.website} target="_blank" rel="noopener noreferrer" onClick={() => trackPartnerContact(partner.name, 'website')} className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-800 text-sm font-semibold transition-colors" data-testid={`partner-site-btn-${partner.id}`}>
        <Globe className="w-4 h-4" aria-hidden="true" /> {partner.website.replace('https://', '')}
      </a>
    </div>
  </article>
);

export const OtherPartnersRow = () => (
  <div data-testid="other-partners-row">
    <h3 className="text-xl sm:text-2xl font-bold text-[#1e2d3b] mb-2">Other Distribution Partners</h3>
    <p className="text-slate-600 mb-6 max-w-prose">Additional authorized partners carrying CTRL Systems products.</p>
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {OTHER_PARTNERS.map((p) => (
        <article
          key={p.website}
          className="flex flex-col rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm hover:border-blue-400 hover:shadow-md transition-all"
          data-testid={`other-partner-${p.slug}`}
        >
          <span className="inline-flex self-start items-center rounded-full bg-slate-100 text-slate-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-5">
            Distribution Partner
          </span>
          <span className="h-16 flex items-center mb-5 rounded-lg bg-slate-900 px-4">
            {p.logo ? (
              <img src={p.logo} alt={p.name} className="max-h-10 w-auto object-contain" loading="lazy" />
            ) : (
              <span className="text-white font-bold tracking-wide text-lg">{p.name}</span>
            )}
          </span>
          <h4 className="text-xl font-bold text-[#1e2d3b]">{p.name}</h4>
          <span className="mt-3 text-slate-700 leading-relaxed">{p.blurb}</span>
          <span className="mt-4 flex flex-col gap-1.5 text-sm">
            {p.email && (
              <a href={`mailto:${p.email}`} onClick={() => trackPartnerContact(p.name, 'email')} className="inline-flex items-center gap-2 text-blue-700 hover:underline" data-testid={`other-partner-email-${p.slug}`}>
                <Mail className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" /> {p.email}
              </a>
            )}
            {p.phone && (
              <a href={`tel:${p.phone.replace(/[^\d+]/g, '')}`} onClick={() => trackPartnerContact(p.name, 'phone')} className="inline-flex items-center gap-2 text-slate-700 hover:underline" data-testid={`other-partner-phone-${p.slug}`}>
                <Phone className="w-3.5 h-3.5 flex-shrink-0 text-blue-600" aria-hidden="true" /> {p.phone}
              </a>
            )}
            <a href={p.website} target="_blank" rel="noopener noreferrer" onClick={() => trackPartnerContact(p.name, 'website')} className="group inline-flex items-center gap-2 font-semibold text-blue-600 hover:underline" data-testid={`other-partner-site-${p.slug}`}>
              <Globe className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" /> {p.website.replace('https://', '')}
              <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-blue-600" aria-hidden="true" />
            </a>
          </span>
        </article>
      ))}
    </div>
  </div>
);

/** Compact "Where to buy" line for product heroes (dark background). */
export const OtherBuyingOptions = ({ testid = 'other-buying-options' }) => (
  <p className="mt-4 flex flex-wrap items-center gap-x-2 text-xs text-blue-100/80" data-testid={testid}>
    <ShoppingBag className="w-3.5 h-3.5 text-orange-400" aria-hidden="true" />
    <Link to="/distribution" className="font-medium text-blue-200 hover:text-white underline underline-offset-4" data-testid={`${testid}-link`}>
      Other Buying Options
    </Link>
    <span className="text-blue-300/60">— all authorized distribution partners</span>
  </p>
);

export const WhereToBuy = ({ testid = 'where-to-buy' }) => (
  <p className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-blue-100/85" data-testid={testid}>
    <ShoppingBag className="w-4 h-4 text-orange-400" aria-hidden="true" />
    <span className="font-semibold text-white">Where to buy:</span>
    {PREFERRED_PARTNERS.map((p, i) => (
      <React.Fragment key={p.id}>
        {i > 0 && <span className="text-blue-300/50">·</span>}
        <a href={`mailto:${p.email}`} onClick={() => trackPartnerContact(p.name, 'email')} className="font-medium text-blue-200 hover:text-white underline-offset-4 hover:underline" data-testid={`${testid}-${p.id}`}>
          {p.name.replace(', Inc.', '')}
        </a>
      </React.Fragment>
    ))}
    <Link to="/distribution" className="text-xs text-blue-300 hover:text-white underline-offset-4 hover:underline" data-testid={`${testid}-all`}>
      All partners
    </Link>
  </p>
);
